import React, { useEffect, useState, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Link, useParams, useRouteMatch } from "react-router-dom";
import MaterialTable from "material-table";
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../../../../utilities/constants";
import { getDoctorsForOrgRequest } from "../../DoctorApiActions";
import { useStyles } from "./styles";
import { getDeptsForOrgWithoutParamsRequest } from "../../../Departments/DepartmentsApiActions";
import defaultUser from "../../../../assets/user.png";
import { debounce } from "../../../../utilities/utilityFunctions";
import ImageLoader from "react-load-image";
import Preloader from "../../../../utilities/components/Preloader/Preloader";
import { ROLES } from "../../../../utilities/constants";
import { getUserInfo } from "../../../../utilities/storage";
import {
  Box,
  Button,
  FormControl,
  Icon,
  MenuItem,
  Select,
  Typography,
  TextField,
  InputLabel,
} from "@material-ui/core";
import { MTableToolbar } from "material-table";
import ActiveStatusIcon from "../../../../utilities/components/ActiveStatusIcon/ActiveStatusIcon";

/**
 * function to render organization doctors list
 * @param getDoctorsForOrg: function to get doctors of organization
 * @returns {JSX.Element}
 * @constructor
 */
const OrganizationDoctors = ({
  getDoctorsForOrg,
  doctors,
  getDeptsForOrgWithoutParams,
  depts,
  orgIdHistory,
  orgNameHistory,
}) => {
  const classes = useStyles();
  const params = useParams();
  const role = getUserInfo().Roles[0].name;
  const match = useRouteMatch();

  const [localFilters, setLocalFilters] = useState({
    name: "",
    email: "",
    phone: "",
    active: "",
  });

  const [apiParams, setApiParams] = useState({
    page: 0,
    rowsPerPage: PAGE_SIZE,
    searchData: {
      name: "",
      email: "",
      phone: "",
      active: "",
    },
    isHideLoader: false,
  });

  const updateApiParams = useCallback(
    debounce((filters) => {
      setApiParams((prev) => ({
        ...prev,
        searchData: filters,
        page: 0,
      }));
    }, 400),
    []
  );

  const handleFilterChange = (field) => (e) => {
    const value = e.target.value;
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    updateApiParams(newFilters);
  };

  const handleSelectChange = (field) => (e) => {
    const value = e.target.value;
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    setApiParams((prev) => ({
      ...prev,
      searchData: newFilters,
      page: 0,
    }));
  };

  useEffect(() => {
    getDeptsForOrgWithoutParams(params.id);
  }, [params.id, getDeptsForOrgWithoutParams]);

  useEffect(() => {
    getDoctorsForOrg(params.id, apiParams, true);
  }, [params.id, apiParams, getDoctorsForOrg]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        title: "Name",
        filtering: false,
        cellStyle: { width: "20%" },
        headerStyle: { width: "20%" },
        render: ({ imageUrl, name, userId }) => (
          <Box
            className={classes.profileContainer}
            component={Link}
            to={{
              pathname: `${match.path.split(":")[0]}doctors/${userId}`,
              state: {
                orgIdHistory,
                orgNameHistory,
              },
            }}
          >
            <Box className={classes.profileInnerContainer}>
              <ImageLoader src={imageUrl || defaultUser}>
                <img alt={userId} className={classes.profileImage} />
                <div className={classes.profileImageError}>{userId}</div>
                <Preloader alt={userId} className={classes.profileImage} />
              </ImageLoader>
            </Box>
            <Box className={classes.nameContainer}>
              <Typography classes={{ root: classes.name }}>{name}</Typography>
            </Box>
          </Box>
        ),
      },
      {
        field: "email",
        title: "Email",
        filtering: false,
        cellStyle: { width: "20%" },
        headerStyle: { width: "20%" },
      },
      {
        field: "phone",
        title: "Phone",
        filtering: false,
        cellStyle: { width: "20%" },
        headerStyle: { width: "20%" },
      },
      {
        field: "role",
        title: "Role/Department",
        filtering: false,
        cellStyle: { width: "15%" },
        headerStyle: { width: "15%" },
        render: ({ role, teams }) => (
          <>
            <div className={classes.roleLabel}>{role}</div>
            {teams?.map((team, index) => (
              <div key={index} className={classes.deptLabel}>
                {team}
              </div>
            ))}
          </>
        ),
      },
      {
        field: "enabled",
        title: "Active Status",
        filtering: false,
        cellStyle: { width: "10%", textAlign: "center" },
        headerStyle: { width: "10%", textAlign: "center" },
        render: ({ enabled, isBanned }) => (
          <ActiveStatusIcon
            enabled={enabled}
            isBanned={isBanned}
            isLabel={false}
          />
        ),
      },
    ],
    [classes, orgIdHistory, orgNameHistory, match.path]
  );

  // Top filter toolbar
  const FilterComponent = useMemo(() => {
    const marginRightValue = role === ROLES.ROLE_CLINIC_OWNER ? 70 : 90;
    return (
      <Box
        display="flex"
        gap={3}
        mb={2}
        sx={{
          "& .MuiFormControl-root, & .MuiTextField-root": {
            marginRight: marginRightValue,
          },
        }}
        style={{ padding: "0 16px", flexWrap: "wrap" }}
      >
        <TextField
          label="Search Name"
          variant="outlined"
          size="small"
          value={localFilters.name}
          onChange={handleFilterChange("name")}
        />
        <TextField
          label="Search Email"
          variant="outlined"
          size="small"
          value={localFilters.email}
          onChange={handleFilterChange("email")}
        />
        <TextField
          label="Search Phone"
          variant="outlined"
          size="small"
          value={localFilters.phone}
          onChange={handleFilterChange("phone")}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={localFilters.active}
            onChange={handleSelectChange("active")}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Blocked</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  }, [localFilters]);

  return (
    <Box>
      {(role === ROLES.ROLE_SYSTEM_ADMIN ||
        role === ROLES.ROLE_CLINIC_OWNER) && (
        <Box className={classes.addButtonContainer}>
          <Button
            disabled={depts.count === 0}
            variant="contained"
            color="primary"
            startIcon={<Icon>add</Icon>}
            disableElevation
            component={Link}
            to={{
              pathname: `${match.path.split(":")[0]}addDoctor`,
              state: { orgIdHistory, orgNameHistory },
            }}
            classes={{ root: classes.addButton }}
          >
            Staff
          </Button>
        </Box>
      )}

      <Box mt={1}>
        {FilterComponent}

        <MaterialTable
          title="Doctors"
          columns={columns}
          data={doctors.rows}
          options={{
            search: false,
            filtering: false,
            sorting: false,
            draggable: false,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            pageSize: apiParams.rowsPerPage,
          }}
          totalCount={doctors.count}
          page={apiParams.page}
          onChangeRowsPerPage={(pageSize) =>
            setApiParams((prev) => ({ ...prev, rowsPerPage: pageSize }))
          }
          onChangePage={(page) => setApiParams((prev) => ({ ...prev, page }))}
          components={{
            Toolbar: (props) => <MTableToolbar {...props} />,
          }}
        />
      </Box>
    </Box>
  );
};
const mapStateToProps = (state) => {
  return {
    doctors: state.doctors.doctors,
    depts: state.departments.depts,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getDoctorsForOrg: (orgId, data, isHideLoader = false) => {
      return dispatch(getDoctorsForOrgRequest(orgId, data, isHideLoader)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
    getDeptsForOrgWithoutParams: (orgId) => {
      return dispatch(getDeptsForOrgWithoutParamsRequest(orgId)).then(
        (res) => {
          return Promise.resolve(res);
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrganizationDoctors);
